import type {
  GroupedSubmissionListResponse,
  Product,
  SubmissionImage,
  SubmissionSource,
} from './types';

type MockGroupedSubmission = GroupedSubmissionListResponse['items'][number];

const submittedAt = '2026-05-01T00:00:00Z';

function mockCurrentProduct(id: number, upc: string, name: string, hasImage = false): Product {
  return {
    id,
    upc,
    name,
    hasImage,
    isSeasonal: false,
    createdAt: submittedAt,
    updatedAt: submittedAt,
    lifecycleStatus: 'active',
    displayUpc: upc,
  };
}

function mockSubmissionImage(id: number, submissionId: number): SubmissionImage {
  return {
    id,
    submissionId,
    imagePath: `uploads/submissions/${submissionId}-${id}.jpg`,
    isPrimary: true,
    imageType: 'front',
    uploadedAt: submittedAt,
  };
}

const reviewDefaults = {
  isRingerPackage: false,
  duplicateMatches: [],
  activeApprovedDuplicateMatches: [],
  blockingReasons: [],
};

const MOCK_GROUPED_SUBMISSIONS: MockGroupedSubmission[] = [
  {
    upc: '012345678901',
    submissionCount: 1,
    submissions: [
      {
        id: 301,
        upc: '012345678901',
        name: 'Michelob Ultra Lime Cactus',
        brand: 'Michelob Ultra',
        status: 'pending',
        isRepurpose: false,
        isUpcChangeRequested: false,
        isImageOnly: false,
        submissionSource: 'user',
        submittedAt: '2026-04-30T14:18:00Z',
        images: [],
        attachments: [],
        ...reviewDefaults,
      },
    ],
    hasConflicts: false,
    conflicts: [],
    currentProduct: mockCurrentProduct(9001, '012345678901', 'Michelob Ultra Lime Cactus'),
  } as MockGroupedSubmission,
  {
    upc: '750000112233',
    submissionCount: 1,
    submissions: [
      {
        id: 302,
        upc: '750000112233',
        name: 'Modelo Oro Shelf Refresh',
        brand: 'Modelo',
        status: 'pending',
        isRepurpose: false,
        isUpcChangeRequested: false,
        isImageOnly: true,
        submissionSource: 'user',
        submittedAt: '2026-04-28T09:42:00Z',
        images: [mockSubmissionImage(11, 302)],
        attachments: [],
        ...reviewDefaults,
      },
    ],
    hasConflicts: false,
    conflicts: [],
    currentProduct: mockCurrentProduct(9002, '750000112233', 'Modelo Oro Shelf Refresh', true),
  } as MockGroupedSubmission,
  {
    upc: '12345678901',
    submissionCount: 1,
    submissions: [
      {
        id: 303,
        upc: '12345678901',
        name: 'Bud Light 12pk Can',
        brand: 'Bud Light',
        status: 'hold',
        isRepurpose: false,
        isUpcChangeRequested: false,
        isImageOnly: false,
        submissionSource: 'iri',
        submittedAt: '2026-04-25T16:05:00Z',
        images: [],
        attachments: [],
        ...reviewDefaults,
        metadata: {
          upcDigitCount: 11,
          iriValidation: {
            status: 'mismatch',
            iriBrand: 'Budweiser',
          },
        },
      },
    ],
    hasConflicts: false,
    conflicts: [],
    currentProduct: mockCurrentProduct(9003, '12345678901', 'Bud Light 12pk Can'),
  } as MockGroupedSubmission,
  {
    upc: '998877665544',
    submissionCount: 1,
    submissions: [
      {
        id: 304,
        upc: '998877665544',
        name: 'Stella Artois Mini Keg',
        brand: 'Stella Artois',
        status: 'approved',
        isRepurpose: false,
        isUpcChangeRequested: false,
        isImageOnly: false,
        submissionSource: 'iri',
        submittedAt: '2026-04-20T11:12:00Z',
        reviewedAt: '2026-04-21T15:33:00Z',
        images: [],
        attachments: [],
        ...reviewDefaults,
      },
    ],
    hasConflicts: false,
    conflicts: [],
    currentProduct: null,
  } as MockGroupedSubmission,
  {
    upc: '665544332211',
    submissionCount: 2,
    submissions: [
      {
        id: 305,
        upc: '665544332211',
        name: 'Corona Familiar Glass',
        brand: 'Corona',
        status: 'pending',
        isRepurpose: true,
        isUpcChangeRequested: true,
        isImageOnly: false,
        submissionSource: 'user',
        submittedAt: '2026-05-01T08:10:00Z',
        images: [],
        attachments: [],
        ...reviewDefaults,
      },
      {
        id: 306,
        upc: '665544332211',
        name: 'Corona Familiar Glass',
        brand: 'Corona',
        status: 'rejected',
        isRepurpose: false,
        isUpcChangeRequested: false,
        isImageOnly: false,
        submissionSource: 'iri',
        submittedAt: '2026-04-27T18:22:00Z',
        reviewedAt: '2026-04-29T13:05:00Z',
        images: [],
        attachments: [],
        ...reviewDefaults,
      },
    ],
    hasConflicts: true,
    conflicts: [],
    currentProduct: mockCurrentProduct(9004, '665544332211', 'Corona Familiar Glass'),
  } as MockGroupedSubmission,
];

export function getDevGroupedSubmissions(
  status?: string,
  page: number = 1,
  perPage: number = 50,
  source?: SubmissionSource
): GroupedSubmissionListResponse {
  const filteredItems = MOCK_GROUPED_SUBMISSIONS.filter((group) => {
    const matchesStatus =
      !status || group.submissions.some((submission) => submission.status === status);
    const matchesSource =
      !source || group.submissions.some((submission) => submission.submissionSource === source);
    return matchesStatus && matchesSource;
  });

  const totalUpcs = filteredItems.length;
  const totalSubmissions = filteredItems.reduce((sum, group) => sum + group.submissionCount, 0);
  const totalPages = Math.max(1, Math.ceil(totalUpcs / perPage));
  const startIndex = (page - 1) * perPage;
  const items = filteredItems.slice(startIndex, startIndex + perPage);

  const allSubmissions = MOCK_GROUPED_SUBMISSIONS.flatMap((group) => group.submissions);

  return {
    items,
    totalUpcs,
    totalSubmissions,
    page,
    perPage,
    totalPages,
    pendingCount: allSubmissions.filter((submission) => submission.status === 'pending').length,
    approvedCount: allSubmissions.filter((submission) => submission.status === 'approved').length,
    rejectedCount: allSubmissions.filter((submission) => submission.status === 'rejected').length,
    holdCount: allSubmissions.filter((submission) => submission.status === 'hold').length,
    needsInfoCount: allSubmissions.filter((submission) => submission.status === 'needs_info')
      .length,
    iriNewCount: allSubmissions.filter(
      (submission) =>
        submission.submissionSource === 'iri' &&
        submission.status === 'pending' &&
        !submission.existingProductId
    ).length,
    iriUpdateCount: allSubmissions.filter(
      (submission) =>
        submission.submissionSource === 'iri' &&
        submission.status === 'pending' &&
        Boolean(submission.existingProductId)
    ).length,
    completedCount: allSubmissions.filter((submission) =>
      ['approved', 'rejected'].includes(submission.status)
    ).length,
  };
}
